import React, { useEffect, useRef, useState } from 'react';
import {
  Container,
  Stack,
  Typography,
  Card,
  Box,
  Alert,
  CircularProgress,
  TextField,
  Button,
} from '@mui/material';
import QrReader from 'react-qr-reader-es6';
import { toast } from 'react-toastify';
import api from '../../api';

const getDeviceLocation = () =>
  new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      reject(new Error('Geolocation is not supported on this device.'));
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        resolve({
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
        });
      },
      (error) => {
        reject(new Error(error?.message || 'Unable to fetch device location.'));
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
    );
  });

export default function ScanOutOfDelivery() {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [adminCity, setAdminCity] = useState('');
  const [adminLoading, setAdminLoading] = useState(true);
  const [manualConsignmentNo, setManualConsignmentNo] = useState('');
  const [lastScanned, setLastScanned] = useState('');
  const [lastLocation, setLastLocation] = useState(null);
  const lastScanAtRef = useRef(0);
  const remarks = adminCity ? `Left hub (${adminCity})` : 'Left hub';

  const fetchAdminDetail = async () => {
    setAdminLoading(true);
    try {
      const response = await api.get('/api/v1/admin_detail');
      const data = response?.data || {};
      const city =
        data?.admin?.city ||
        data?.adminDetail?.city ||
        data?.admin_detail?.city ||
        data?.city ||
        '';

      if (!city) {
        toast.error('Admin city not found. Please update profile.');
        return;
      }

      setAdminCity(city);
    } catch (error) {
      toast.error(error?.response?.data?.message || error?.message || 'Failed to fetch admin details.');
    } finally {
      setAdminLoading(false);
    }
  };

  useEffect(() => {
    fetchAdminDetail();
  }, []);

  const submitOutForDelivery = async (consignmentNo) => {
    if (!consignmentNo || isSubmitting || !adminCity) return;
    if (!remarks || !String(remarks).trim()) {
      toast.error('Please enter remarks.');
      return;
    }

    try {
      setIsSubmitting(true);
      const { latitude, longitude } = await getDeviceLocation();
      setLastLocation({ latitude, longitude });

      const response = await api.post('/api/v1/deliveries/out_for_delivery', {
        data: {
          consignmentNo,
          remarks: String(remarks).trim(),
          latitude,
          longitude,
        },
      });

      if (response?.data?.success) {
        setLastScanned(consignmentNo);
        toast.success(response?.data?.message || 'Marked as out for delivery.');
      } else {
        toast.error(response?.data?.message || 'Failed to submit out for delivery.');
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || error?.message || 'Failed to submit out for delivery.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleScan = async (data) => {
    if (!data || isSubmitting) return;

    const now = Date.now();
    if (now - lastScanAtRef.current < 200) return;

    const rawValue = typeof data === 'string' ? data : data?.text;
    const consignmentNo = String(rawValue || '').trim();
    if (!consignmentNo) return;

    lastScanAtRef.current = now;
    await submitOutForDelivery(consignmentNo);
  };

  const handleError = (err) => {
    console.error(err);
  };

  const onClickManualSubmit = async () => {
    const consignmentNo = String(manualConsignmentNo || '').trim();
    if (!consignmentNo) {
      toast.error('Please enter consignment number.');
      return;
    }
    await submitOutForDelivery(consignmentNo);
    setManualConsignmentNo('');
  };

  return (
    <Container maxWidth="sm">
      <Stack direction="row" alignItems="center" justifyContent="space-between" mb={5}>
        <Typography variant="h4" gutterBottom>
          Scan Out Of Delivery
        </Typography>
      </Stack>

      <Card sx={{ p: { xs: 2, sm: 3 } }}>
        <Stack spacing={2}>
          <Typography variant="h6">QR Code Scanner</Typography>
          <Typography variant="body2" color="text.secondary">
            Use your phone&apos;s back camera to scan and mark consignment out for delivery.
          </Typography>

          <TextField
            label="Remarks"
            value={remarks}
            fullWidth
            InputProps={{ readOnly: true }}
          />

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
            <TextField
              label="Manual Consignment Number"
              value={manualConsignmentNo}
              onChange={(e) => setManualConsignmentNo(e.target.value)}
              fullWidth
            />
            <Button variant="contained" onClick={onClickManualSubmit} disabled={isSubmitting || !adminCity}>
              Submit
            </Button>
          </Stack>

          <Box
            sx={{
              width: '100%',
              maxWidth: 420,
              mx: 'auto',
              borderRadius: 2,
              overflow: 'hidden',
              border: '1px solid',
              borderColor: 'divider',
              bgcolor: 'common.black',
            }}
          >
            <QrReader
              delay={250}
              onError={handleError}
              onScan={adminCity ? handleScan : () => {}}
              constraints={{ facingMode: { ideal: 'environment' } }}
              style={{ width: '100%' }}
            />
          </Box>

          {adminLoading && (
            <Stack direction="row" spacing={1} alignItems="center">
              <CircularProgress size={18} />
              <Typography variant="body2" color="text.secondary">
                Loading admin details...
              </Typography>
            </Stack>
          )}

          {isSubmitting && (
            <Stack direction="row" spacing={1} alignItems="center">
              <CircularProgress size={18} />
              <Typography variant="body2" color="text.secondary">
                Submitting out-for-delivery update...
              </Typography>
            </Stack>
          )}

          {lastScanned && (
            <Alert severity="success">
              Last submitted consignment: <b>{lastScanned}</b>
            </Alert>
          )}

          {lastLocation && (
            <Alert severity="info">
              Location used: <b>{lastLocation.latitude.toFixed(6)}</b>, <b>{lastLocation.longitude.toFixed(6)}</b>
            </Alert>
          )}
        </Stack>
      </Card>
    </Container>
  );
}
